import type React from "react";
import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Sector,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area,
  LineChart,
  Line,
  ComposedChart,
} from "recharts";
import {
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  PieChartIcon,
  ChevronDown,
  Calendar,
} from "lucide-react";
import useApiHook from "../../../../utils/useApi";

interface ApiDataItem {
  startDate: string;
  endDate: string;
  successCount: number;
  failedCount: number;
  abandonedCount: number;
}

interface ApiResponse {
  statusCode: string;
  status: string;
  description: string;
  timestamp: string | null;
  errorCode: string | null;
  data: { data: ApiDataItem[] | null } | null;
}

interface TransactionData {
  name: string;
  value: number;
  color: string;
  gradient: string[];
  trend: number;
}

interface TrendData {
  name: string;
  successful: number;
  failed: number;
  canceled: number;
}

type FilterType = "DAY" | "WEEK" | "MONTH";

const TransactionGraphWithTrends: React.FC = () => {
  const [selectedRange, setSelectedRange] = useState<string>("7");
  const [selectedType, setSelectedType] = useState<FilterType>("DAY");
  const [selectedStartDate, setSelectedStartDate] = useState<string>(() => {
    const today = new Date();
    const day = today.getDate().toString().padStart(2, "0");
    const month = (today.getMonth() + 1).toString().padStart(2, "0");
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  });
  const [showRangeDropdown, setShowRangeDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<"pie" | "trend">("trend");
  const [chartType, setChartType] = useState<
    "bar" | "area" | "line" | "composed"
  >("line");
  const [animateChart, setAnimateChart] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);

  const { makeRequest } = useApiHook<ApiResponse>(
    "/dashboard/payments/analytics",
    {
      method: "post",
    }
  );

  const getRangeOptions = (type: FilterType): string[] => {
    switch (type) {
      case "DAY":
        return ["1", "2", "3", "4", "5", "6", "7"];
      case "WEEK":
        return ["1", "2", "3", "4"];
      case "MONTH":
        return ["1", "2", "3", "4"];
      default:
        return ["1"];
    }
  };

  const {
    data: apiData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "transactionAnalytics",
      selectedRange,
      selectedType,
      selectedStartDate,
    ],
    queryFn: async () => {
      const payload = {
        range: selectedRange,
        startDate: selectedStartDate,
        type: selectedType,
      };

      return await makeRequest(payload);
    },
    refetchInterval: 30000,
    refetchIntervalInBackground: true,
    staleTime: 10000,
  });

  // Process API data for pie chart
  const pieData = useMemo((): TransactionData[] => {
    if (
      !apiData?.data?.data ||
      !Array.isArray(apiData.data.data) ||
      apiData.data.data.length === 0
    ) {
      return [];
    }

    const totals = apiData.data.data.reduce(
      (
        acc: { successful: any; failed: any; abandoned: any },
        item: { successCount: any; failedCount: any; abandonedCount: any }
      ) => ({
        successful: acc.successful + item.successCount,
        failed: acc.failed + item.failedCount,
        abandoned: acc.abandoned + item.abandonedCount,
      }),
      { successful: 0, failed: 0, abandoned: 0 }
    );

    // Calculate trends (comparing first half vs second half)
    const midPoint = Math.floor(apiData.data.data.length / 2);
    const firstHalf = apiData.data.data.slice(0, midPoint);
    const secondHalf = apiData.data.data.slice(midPoint);

    const firstHalfTotals = firstHalf.reduce(
      (
        acc: { successful: any; failed: any; abandoned: any },
        item: { successCount: any; failedCount: any; abandonedCount: any }
      ) => ({
        successful: acc.successful + item.successCount,
        failed: acc.failed + item.failedCount,
        abandoned: acc.abandoned + item.abandonedCount,
      }),
      { successful: 0, failed: 0, abandoned: 0 }
    );

    const secondHalfTotals = secondHalf.reduce(
      (
        acc: { successful: any; failed: any; abandoned: any },
        item: { successCount: any; failedCount: any; abandonedCount: any }
      ) => ({
        successful: acc.successful + item.successCount,
        failed: acc.failed + item.failedCount,
        abandoned: acc.abandoned + item.abandonedCount,
      }),
      { successful: 0, failed: 0, abandoned: 0 }
    );

    const calculateTrend = (first: number, second: number): number => {
      if (first === 0 && second === 0) return 0;
      if (first === 0) return second > 0 ? 100 : 0;
      return ((second - first) / first) * 100;
    };

    return [
      {
        name: "Successful",
        value: totals.successful,
        color: "#10B981",
        gradient: ["#10B981", "#059669"],
        trend: calculateTrend(
          firstHalfTotals.successful,
          secondHalfTotals.successful
        ),
      },
      {
        name: "Failed",
        value: totals.failed,
        color: "#EF4444",
        gradient: ["#F87171", "#DC2626"],
        trend: calculateTrend(firstHalfTotals.failed, secondHalfTotals.failed),
      },
      {
        name: "Canceled",
        value: totals.abandoned,
        color: "#F59E0B",
        gradient: ["#FBBF24", "#D97706"],
        trend: calculateTrend(
          firstHalfTotals.abandoned,
          secondHalfTotals.abandoned
        ),
      },
    ];
  }, [apiData]);

  // Process API data for trend chart
  const trendData = useMemo((): TrendData[] => {
    if (
      !apiData?.data?.data ||
      !Array.isArray(apiData.data.data) ||
      apiData.data.data.length === 0
    ) {
      return [];
    }

    return apiData.data.data.map(
      (
        item: { successCount: any; failedCount: any; abandonedCount: any },
        index: number
      ) => {
        // Since API returns consistent dates, use index-based labeling for clarity
        let name: string;

        if (selectedType === "DAY") {
          name = `Day ${index + 1}`;
        } else if (selectedType === "WEEK") {
          name = `Week ${index + 1}`;
        } else {
          name = `Month ${index + 1}`;
        }

        return {
          name,
          successful: item.successCount || 0,
          failed: item.failedCount || 0,
          canceled: item.abandonedCount || 0,
        };
      }
    );
  }, [apiData, selectedType]);

  const totalTransactions = pieData.reduce(
    (sum, entry) => sum + entry.value,
    0
  );

  useEffect(() => {
    setAnimateChart(true);
    const timer = setTimeout(() => setAnimateChart(false), 800);
    return () => clearTimeout(timer);
  }, [activeTab, chartType]);

  useEffect(() => {
    // Reset range to "1" when type changes to ensure it's always valid
    setSelectedRange("1");
  }, [selectedType]);

  const formatDateForInput = (dateStr: string): string => {
    const [day, month, year] = dateStr.split("-");
    return `${year}-${month}-${day}`;
  };

  const formatDateForPayload = (dateStr: string): string => {
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const value = payload[0].value || 0;
      const percentage =
        totalTransactions > 0
          ? ((value / totalTransactions) * 100).toFixed(1)
          : "0";

      return (
        <div className="bg-white/95 backdrop-blur-sm p-4 shadow-xl rounded-lg border border-gray-200 transition-all duration-300">
          <p className="font-semibold text-gray-900 mb-1">
            {data.name || payload[0].name}
          </p>
          <p className="text-sm text-gray-600">
            {value.toLocaleString()} transactions
          </p>
          <p
            className="text-sm font-medium mt-1"
            style={{ color: payload[0].color || data.color }}
          >
            {percentage}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  const renderActiveShape = (props: any) => {
    const {
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      value,
    } = props;

    const percentage =
      totalTransactions > 0
        ? ((value / totalTransactions) * 100).toFixed(1)
        : "0";
    const sin = Math.sin((-startAngle * Math.PI) / 180);
    const cos = Math.cos((-startAngle * Math.PI) / 180);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 6}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius - 4}
          outerRadius={outerRadius + 12}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          opacity={0.2}
        />
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
          strokeWidth={2}
        />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          textAnchor={textAnchor}
          fill="#333"
          className="text-sm font-medium"
        >
          {payload.name}
        </text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#999"
          className="text-xs"
        >
          {value.toLocaleString()} ({percentage}%)
        </text>
        <text
          x={cx}
          y={cy - 5}
          textAnchor="middle"
          fill="#333"
          className="text-lg font-bold"
        >
          {percentage}%
        </text>
        <text
          x={cx}
          y={cy + 20}
          textAnchor="middle"
          fill="#666"
          className="text-xs"
        >
          {value.toLocaleString()}
        </text>
      </g>
    );
  };

  // Handle pie sector hover
  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  // Trend indicator component
  const renderTrendIndicator = (trend: number, index: number) => {
    if (trend > 0) {
      return (
        <div
          className={`flex items-center text-emerald-600 transition-all duration-300 ${
            hoveredItem === index ? "scale-110" : ""
          }`}
        >
          <ArrowUpRight className="h-4 w-4 mr-1" />
          <span>{trend.toFixed(1)}%</span>
        </div>
      );
    } else if (trend < 0) {
      return (
        <div
          className={`flex items-center text-red-600 transition-all duration-300 ${
            hoveredItem === index ? "scale-110" : ""
          }`}
        >
          <ArrowDownRight className="h-4 w-4 mr-1" />
          <span>{Math.abs(trend).toFixed(1)}%</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center text-gray-500">
          <span>0%</span>
        </div>
      );
    }
  };

  const renderTrendChart = () => {
    if (
      !apiData?.data?.data ||
      !Array.isArray(apiData.data.data) ||
      apiData.data.data.length === 0
    ) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="text-gray-400 mb-2">
              <TrendingUp className="h-12 w-12 mx-auto" />
            </div>
            {/* <p className="text-gray-500">No data available</p> */}
            <p className="text-sm text-gray-400">Data is being fetched...</p>
          </div>
        </div>
      );
    }

    const chartProps = {
      data: trendData,
      margin: { top: 20, right: 30, left: 20, bottom: 5 },
    };

    switch (chartType) {
      case "area":
        return (
          <AreaChart {...chartProps}>
            <defs>
              <linearGradient id="successGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#10B981" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="failedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#EF4444" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="canceledGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#F59E0B" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#f0f0f0"
            />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="successful"
              name="Successful"
              stroke="#10B981"
              fill="url(#successGradient)"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="failed"
              name="Failed"
              stroke="#EF4444"
              fill="url(#failedGradient)"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="canceled"
              name="Canceled"
              stroke="#F59E0B"
              fill="url(#canceledGradient)"
              strokeWidth={2}
            />
          </AreaChart>
        );
      case "line":
        return (
          <LineChart {...chartProps}>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#f0f0f0"
            />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="successful"
              name="Successful"
              stroke="#10B981"
              strokeWidth={3}
              dot={{ fill: "#10B981", strokeWidth: 2, r: 6, stroke: "#fff" }}
            />
            <Line
              type="monotone"
              dataKey="failed"
              name="Failed"
              stroke="#EF4444"
              strokeWidth={3}
              dot={{ fill: "#EF4444", strokeWidth: 2, r: 6, stroke: "#fff" }}
            />
            <Line
              type="monotone"
              dataKey="canceled"
              name="Canceled"
              stroke="#F59E0B"
              strokeWidth={3}
              dot={{ fill: "#F59E0B", strokeWidth: 2, r: 6, stroke: "#fff" }}
            />
          </LineChart>
        );
      case "composed":
        return (
          <ComposedChart {...chartProps}>
            <defs>
              <linearGradient id="canceledGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#F59E0B" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#f0f0f0"
            />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="successful"
              name="Successful"
              fill="#10B981"
              radius={[4, 4, 0, 0]}
            />
            <Line
              type="monotone"
              dataKey="failed"
              name="Failed"
              stroke="#EF4444"
              strokeWidth={3}
            />
            <Area
              type="monotone"
              dataKey="canceled"
              name="Canceled"
              fill="url(#canceledGradient)"
              stroke="#F59E0B"
            />
          </ComposedChart>
        );
      default:
        return (
          <BarChart {...chartProps}>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#f0f0f0"
            />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="successful"
              name="Successful"
              fill="#10B981"
              radius={[6, 6, 0, 0]}
            />
            <Bar
              dataKey="failed"
              name="Failed"
              fill="#EF4444"
              radius={[6, 6, 0, 0]}
            />
            <Bar
              dataKey="canceled"
              name="Canceled"
              fill="#F59E0B"
              radius={[6, 6, 0, 0]}
            />
          </BarChart>
        );
    }
  };

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-red-200 p-6">
        <div className="text-center">
          <div className="text-red-400 mb-2">
            <TrendingUp className="h-12 w-12 mx-auto" />
          </div>
          <p className="text-red-600 font-medium">
            Failed to load transaction data
          </p>
          <p className="text-sm text-red-400 mt-1">Please try again later</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 sm:p-6 transition-all duration-500 hover:shadow-xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              Transaction Analytics
            </h2>
            <p className="text-gray-500 text-sm">
              Real-time overview of transaction outcomes
              {apiData?.data?.data && (
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                  Live Data
                </span>
              )}
              {isLoading && (
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  Updating...
                </span>
              )}
            </p>
          </div>

          {/* Chart Type Toggle */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            <button
              className={`px-4 py-2 text-sm font-medium transition-all duration-300 ${
                activeTab === "pie"
                  ? "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => setActiveTab("pie")}
            >
              <div className="flex items-center">
                <PieChartIcon className="h-4 w-4 mr-1" />
                Distribution
              </div>
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium transition-all duration-300 ${
                activeTab === "trend"
                  ? "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => setActiveTab("trend")}
            >
              <div className="flex items-center">
                <TrendingUp className="h-4 w-4 mr-1" />
                Trends
              </div>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          {/* Start Date Filter */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={formatDateForInput(selectedStartDate)}
              onChange={(e) =>
                setSelectedStartDate(formatDateForPayload(e.target.value))
              }
              className="w-full sm:w-40 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
            />
          </div>

          {/* Type Filter */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Type
            </label>
            <button
              onClick={() => {
                setShowTypeDropdown(!showTypeDropdown);
                setShowRangeDropdown(false);
              }}
              className="flex items-center justify-between w-full sm:w-32 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors duration-200"
            >
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                <span className="text-sm font-medium">{selectedType}</span>
              </div>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </button>
            {showTypeDropdown && (
              <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                {(["DAY", "WEEK", "MONTH"] as FilterType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => {
                      setSelectedType(type);
                      setShowTypeDropdown(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors duration-200 ${
                      selectedType === type
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Range Filter */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Range
            </label>
            <button
              onClick={() => {
                setShowRangeDropdown(!showRangeDropdown);
                setShowTypeDropdown(false);
              }}
              className="flex items-center justify-between w-full sm:w-24 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors duration-200"
            >
              <span className="text-sm font-medium">{selectedRange}</span>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </button>
            {showRangeDropdown && (
              <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                {getRangeOptions(selectedType).map((range) => (
                  <button
                    key={range}
                    onClick={() => {
                      setSelectedRange(range);
                      setShowRangeDropdown(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors duration-200 ${
                      selectedRange === range
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                  >
                    {range}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Chart Type Selector for Trends */}
          {activeTab === "trend" && (
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Chart Type
              </label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                {(["bar", "area", "line", "composed"] as const).map((type) => (
                  <button
                    key={type}
                    className={`px-3 py-2 text-xs font-medium transition-all duration-300 ${
                      chartType === type
                        ? "bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800"
                        : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                    onClick={() => setChartType(type)}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Charts */}
      <div
        className={`transition-opacity duration-500 ${
          animateChart ? "opacity-0" : "opacity-100"
        }`}
      >
        {activeTab === "pie" ? (
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="w-full lg:w-1/2 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <defs>
                    {pieData.map((entry, index) => (
                      <linearGradient
                        key={`gradient-${index}`}
                        id={`pieGradient${index}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor={entry.gradient[0]}
                          stopOpacity={1}
                        />
                        <stop
                          offset="100%"
                          stopColor={entry.gradient[1]}
                          stopOpacity={1}
                        />
                      </linearGradient>
                    ))}
                  </defs>
                  <Pie
                    activeIndex={activeIndex}
                    activeShape={renderActiveShape}
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    dataKey="value"
                    onMouseEnter={onPieEnter}
                    onMouseLeave={() => setActiveIndex(undefined)}
                    paddingAngle={2}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={`url(#pieGradient${index})`}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="w-full lg:w-1/2">
              <div className="grid grid-cols-1 gap-4">
                {pieData.map((item, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border flex items-center justify-between transition-all duration-300 hover:shadow-md ${
                      hoveredItem === index ? "bg-gray-50 scale-[1.02]" : ""
                    }`}
                    style={{
                      borderColor: item.color + "40",
                      background:
                        hoveredItem === index
                          ? `linear-gradient(to right, white, ${item.color}10)`
                          : "white",
                    }}
                    onMouseEnter={() => setHoveredItem(index)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{
                          background: `linear-gradient(135deg, ${item.gradient[0]}, ${item.gradient[1]})`,
                        }}
                      />
                      <span className="font-medium text-gray-700">
                        {item.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      {renderTrendIndicator(item.trend, index)}
                      <div className="flex flex-col items-end">
                        <span className="font-bold text-gray-800">
                          {item.value.toLocaleString()}
                        </span>
                        <span className="text-xs text-gray-500">
                          {totalTransactions > 0
                            ? ((item.value / totalTransactions) * 100).toFixed(
                                1
                              )
                            : 0}
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="p-4 rounded-lg border border-gray-200 flex items-center justify-between bg-gradient-to-r from-gray-50 to-gray-100">
                  <span className="font-medium text-gray-700">
                    Total Transactions
                  </span>
                  <span className="font-bold text-gray-800">
                    {totalTransactions.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              {renderTrendChart()}
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Click outside to close dropdowns */}
      {(showRangeDropdown || showTypeDropdown) && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => {
            setShowRangeDropdown(false);
            setShowTypeDropdown(false);
          }}
        />
      )}
    </div>
  );
};

export default TransactionGraphWithTrends;
